import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Wrench, Camera, Upload, CheckSquare, FileSignature, Loader2, MapPin,
  PlayCircle, CheckCircle2, ChevronRight,
} from "lucide-react";
import PWAInstallBanner from "@/components/PWAInstallBanner";

const PRE_CAL_CHECKLIST = [
  { key: "alignment", label: "Wheel alignment OK" },
  { key: "weight", label: "Fuel ≥ ½ + spare tire in" },
  { key: "tires", label: "Tire pressure to spec" },
  { key: "level", label: "Vehicle on level ground" },
  { key: "battery", label: "Battery healthy (charger if needed)" },
  { key: "obstructions", label: "Bumper/grille clean, no stickers" },
  { key: "targets", label: "Targets positioned per OEM" },
];
const POST_CAL_CHECKLIST = [
  { key: "no_dtc", label: "No new DTCs" },
  { key: "road_test", label: "Road test passed" },
  { key: "warning_lights", label: "No warning lights" },
  { key: "scan_done", label: "Post-scan attached" },
];

export default function TechApp() {
  const [queue, setQueue] = useState({ in_progress: [], queue: [], done_today: 0 });
  const [active, setActive] = useState(null);

  const load = () => api.get("/tech/my-queue").then((r) => setQueue(r.data));
  useEffect(() => { load(); }, []);

  const claim = async (j) => {
    try {
      await api.post(`/tech/jobs/${j.id}/claim`);
      toast.success("Job claimed");
      setActive(j); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to claim"); }
  };

  if (active) return <TechJob job={active} onClose={() => { setActive(null); load(); }} />;

  return (
    <div className="min-h-screen bg-[#030712] text-slate-100" data-testid="tech-app">
      <PWAInstallBanner />
      <div className="sticky top-0 bg-[#050a17] border-b border-slate-800 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Wrench className="w-5 h-5 text-blue-400" />
          <div className="font-display font-semibold">Tech Field App</div>
        </div>
        <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/20">{queue.done_today} done today</Badge>
      </div>

      <div className="p-4 space-y-4 max-w-2xl mx-auto">
        {queue.in_progress.length > 0 && (
          <section>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-amber-400 mb-2">⚡ In Progress</div>
            <div className="space-y-2">
              {queue.in_progress.map((j) => (
                <JobCard key={j.id} j={j} onTap={() => setActive(j)} active />
              ))}
            </div>
          </section>
        )}
        <section>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2">📋 Queue ({queue.queue.length})</div>
          <div className="space-y-2">
            {queue.queue.length === 0 && <div className="text-center text-slate-500 py-8 text-sm">Nothing in queue</div>}
            {queue.queue.map((j) => (
              <JobCard key={j.id} j={j} onClaim={() => claim(j)} />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

const JobCard = ({ j, onTap, onClaim, active }) => (
  <div data-testid={`tech-job-${j.id}`} className={`rounded-xl border ${active ? "border-amber-500/30 bg-amber-500/5" : "border-slate-800 bg-[#0B1120]"} p-4`}>
    <div className="flex items-start justify-between gap-3">
      <div className="flex-1 min-w-0">
        <div className="font-mono text-xs text-blue-300">{j.vin}</div>
        <div className="text-sm font-medium mt-1 truncate">{j.customer_name}</div>
        <div className="text-xs text-slate-500 mt-0.5">{j.vehicle_year} {j.vehicle_make} {j.vehicle_model}</div>
        <div className="flex gap-2 mt-2">
          <Badge className="bg-slate-800 border-slate-700 text-slate-300 text-[10px]">{j.status}</Badge>
          {j.insurance_company && <Badge className="bg-cyan-500/15 text-cyan-300 border-cyan-500/20 text-[10px]">{j.insurance_company}</Badge>}
        </div>
      </div>
      {active ? (
        <Button data-testid={`tech-open-${j.id}`} size="sm" onClick={onTap} className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold">
          Open <ChevronRight className="w-3 h-3 ml-1" />
        </Button>
      ) : (
        <Button data-testid={`tech-claim-${j.id}`} size="sm" onClick={onClaim} className="bg-blue-600 hover:bg-blue-500">
          Claim <PlayCircle className="w-3 h-3 ml-1" />
        </Button>
      )}
    </div>
  </div>
);

const TechJob = ({ job, onClose }) => {
  const [preList, setPreList] = useState({});
  const [postList, setPostList] = useState({});
  const [notes, setNotes] = useState("");
  const [preDone, setPreDone] = useState(false);
  const [postDone, setPostDone] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const photoRef = useRef(null);
  const scanRef = useRef(null);
  const [completed, setCompleted] = useState(false);

  const uploadPhoto = async (category) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append("category", category);
      fd.append("file", file);
      // try to capture GPS
      if (navigator.geolocation) {
        try {
          const pos = await new Promise((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, { timeout: 3000 }));
          fd.append("gps_lat", pos.coords.latitude);
          fd.append("gps_lng", pos.coords.longitude);
        } catch {}
      }
      await api.post(`/tech/jobs/${job.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Photo (${category}) uploaded`);
    } catch (err) { toast.error("Upload failed"); } finally { setPhotoUploading(false); e.target.value = ""; }
  };

  const uploadScan = (scanType) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("scan_type", scanType);
    fd.append("file", file);
    try {
      await api.post(`/tech/jobs/${job.id}/scan`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${scanType.toUpperCase()}-scan uploaded`);
    } catch { toast.error("Upload failed"); } finally { e.target.value = ""; }
  };

  const submitChecklist = async (type, items, doneSetter) => {
    const list = type === "pre_cal" ? PRE_CAL_CHECKLIST : POST_CAL_CHECKLIST;
    const payload = list.map((it) => ({ key: it.key, label: it.label, checked: !!items[it.key] }));
    await api.post(`/tech/jobs/${job.id}/checklist`, { checklist_type: type, items: payload, notes });
    toast.success(`${type.replace("_", " ")} submitted`);
    doneSetter(true);
  };

  const complete = async () => {
    if (!preDone) return toast.error("Submit pre-cal checklist first");
    await api.post(`/tech/jobs/${job.id}/complete`);
    toast.success("Job marked complete!");
    setCompleted(true);
  };

  return (
    <div className="min-h-screen bg-[#030712] text-slate-100" data-testid="tech-job-page">
      <div className="sticky top-0 bg-[#050a17] border-b border-slate-800 px-4 py-3 flex items-center justify-between">
        <button data-testid="tech-back-btn" onClick={onClose} className="text-blue-400 text-sm">← Back</button>
        <div className="font-mono text-xs text-blue-300 truncate">{job.vin}</div>
      </div>

      <div className="p-4 space-y-4 max-w-2xl mx-auto">
        <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-4">
          <div className="text-sm font-medium">{job.customer_name}</div>
          <div className="text-xs text-slate-500 mt-1">{job.vehicle_year} {job.vehicle_make} {job.vehicle_model}</div>
        </div>

        {!completed && (
          <>
            <Section title="📷 Photos" testId="section-photos">
              <input ref={photoRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={uploadPhoto("damage")()} data-testid="photo-damage-input" />
              <div className="grid grid-cols-2 gap-2">
                <PhotoBtn label="Damage / Before" onClick={() => photoRef.current?.click()} icon={Camera} loading={photoUploading} />
                <PhotoBtn label="Target Setup" onClick={() => document.getElementById("photo-target")?.click()} icon={Camera} />
                <PhotoBtn label="Scan Tool" onClick={() => document.getElementById("photo-scan")?.click()} icon={Camera} />
                <PhotoBtn label="After / Success" onClick={() => document.getElementById("photo-after")?.click()} icon={Camera} />
              </div>
              <input id="photo-target" type="file" accept="image/*" capture="environment" className="hidden" onChange={uploadPhoto("target")()} />
              <input id="photo-scan" type="file" accept="image/*" capture="environment" className="hidden" onChange={uploadPhoto("scan")()} />
              <input id="photo-after" type="file" accept="image/*" capture="environment" className="hidden" onChange={uploadPhoto("after")()} />
            </Section>

            <Section title="📄 Scan Reports" testId="section-scans">
              <div className="grid grid-cols-2 gap-2">
                <PhotoBtn label="Upload Pre-Scan" onClick={() => document.getElementById("scan-pre")?.click()} icon={Upload} />
                <PhotoBtn label="Upload Post-Scan" onClick={() => document.getElementById("scan-post")?.click()} icon={Upload} />
              </div>
              <input id="scan-pre" type="file" accept=".pdf" className="hidden" onChange={uploadScan("pre")()} />
              <input id="scan-post" type="file" accept=".pdf" className="hidden" onChange={uploadScan("post")()} />
            </Section>

            <Section title="✅ Pre-Cal Checklist" testId="section-pre-checklist">
              {PRE_CAL_CHECKLIST.map((it) => (
                <label key={it.key} className="flex items-center gap-3 text-sm py-2 border-b border-slate-800/60 last:border-0">
                  <input data-testid={`pre-${it.key}`} type="checkbox" checked={!!preList[it.key]} onChange={(e) => setPreList({ ...preList, [it.key]: e.target.checked })} className="w-5 h-5" />
                  <span className={preList[it.key] ? "text-slate-200" : "text-slate-400"}>{it.label}</span>
                </label>
              ))}
              <Button data-testid="pre-submit-btn" disabled={preDone} onClick={() => submitChecklist("pre_cal", preList, setPreDone)} className="mt-3 w-full bg-blue-600 hover:bg-blue-500">
                {preDone ? <><CheckCircle2 className="w-4 h-4 mr-2" />Submitted</> : <><CheckSquare className="w-4 h-4 mr-2" />Submit Pre-Cal</>}
              </Button>
            </Section>

            <Section title="✅ Post-Cal Checklist" testId="section-post-checklist">
              {POST_CAL_CHECKLIST.map((it) => (
                <label key={it.key} className="flex items-center gap-3 text-sm py-2 border-b border-slate-800/60 last:border-0">
                  <input data-testid={`post-${it.key}`} type="checkbox" checked={!!postList[it.key]} onChange={(e) => setPostList({ ...postList, [it.key]: e.target.checked })} className="w-5 h-5" />
                  <span className={postList[it.key] ? "text-slate-200" : "text-slate-400"}>{it.label}</span>
                </label>
              ))}
              <Button data-testid="post-submit-btn" disabled={postDone} onClick={() => submitChecklist("post_cal", postList, setPostDone)} className="mt-3 w-full bg-blue-600 hover:bg-blue-500">
                {postDone ? <><CheckCircle2 className="w-4 h-4 mr-2" />Submitted</> : <><CheckSquare className="w-4 h-4 mr-2" />Submit Post-Cal</>}
              </Button>
            </Section>

            <Section title="📝 Notes" testId="section-notes">
              <textarea
                data-testid="job-notes"
                placeholder="Any issues or observations..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full bg-[#030712] border border-slate-800 rounded-lg p-3 text-sm min-h-[80px]"
              />
            </Section>

            <Button data-testid="complete-job-btn" onClick={complete} className="w-full bg-emerald-600 hover:bg-emerald-500 h-12 text-base font-semibold">
              <CheckCircle2 className="w-5 h-5 mr-2" />Complete Job
            </Button>
          </>
        )}
        {completed && (
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-6 text-center">
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
            <div className="font-display text-xl font-semibold text-emerald-300">Job Complete!</div>
            <div className="text-xs text-slate-400 mt-1">Great work. Tap "Back" to claim the next one.</div>
          </div>
        )}
      </div>
    </div>
  );
};

const Section = ({ title, children, testId }) => (
  <section data-testid={testId} className="rounded-xl border border-slate-800 bg-[#0B1120] p-4">
    <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-3">{title}</div>
    {children}
  </section>
);

const PhotoBtn = ({ label, onClick, icon: Icon, loading }) => (
  <button onClick={onClick} className="rounded-lg border border-slate-800 bg-slate-900/40 hover:border-blue-500/40 p-4 flex flex-col items-center gap-2 transition-colors">
    {loading ? <Loader2 className="w-5 h-5 animate-spin text-blue-400" /> : <Icon className="w-5 h-5 text-blue-400" />}
    <span className="text-xs text-slate-300">{label}</span>
  </button>
);
